/**
 * Facilities, Entrega 3c-1 (ADR 0036): parâmetros configuráveis (prazo de reabertura dos chamados
 * e evidências mínimas por tipo de OS) e os indicadores da manutenção. Prazos de atendimento não
 * ficam aqui: são do SLA corporativo (ADR 0061).
 */
import { z } from 'zod';
import { FACILITIES_REOPEN_DAYS } from './facilities.ts';
import { WORK_ORDER_KINDS, WORK_ORDER_REQUIRED_EVIDENCE } from './facilities-maintenance.ts';

/** Categorias de anexo que podem ser exigidas para concluir uma OS (o checklist tem as dele). */
export const REQUIRABLE_EVIDENCE = ['photo_before', 'photo_after', 'report', 'receipt'] as const;
export type RequirableEvidence = (typeof REQUIRABLE_EVIDENCE)[number];

export const REOPEN_DAYS_MIN = 1;
export const REOPEN_DAYS_MAX = 365;

/** Valores iniciais (os de antes da 3c-1): 30 dias e foto depois nas corretivas. */
export const FACILITIES_DEFAULT_SETTINGS = {
  reopenDays: FACILITIES_REOPEN_DAYS,
  requiredEvidence: WORK_ORDER_REQUIRED_EVIDENCE as Record<string, readonly string[]>,
};

const evidenceList = z
  .array(z.enum(REQUIRABLE_EVIDENCE))
  .max(REQUIRABLE_EVIDENCE.length)
  .refine((v) => new Set(v).size === v.length, 'Categoria repetida');

/**
 * Mudança dos parâmetros: só o que mudar (o resto fica como está) e sempre com o motivo, que vai
 * para o histórico.
 */
export const facilitiesSettingsInput = z
  .object({
    reopenDays: z.number().int().min(REOPEN_DAYS_MIN).max(REOPEN_DAYS_MAX).optional(),
    requiredEvidence: z
      .object(Object.fromEntries(WORK_ORDER_KINDS.map((k) => [k, evidenceList])))
      .optional(),
    reason: z.string().trim().min(5).max(2000),
  })
  .refine((v) => v.reopenDays !== undefined || v.requiredEvidence !== undefined, {
    path: ['reopenDays'],
    message: 'Nada a alterar',
  });
export type FacilitiesSettingsInput = z.infer<typeof facilitiesSettingsInput>;

/** Evidências exigidas para o tipo de OS, pelos parâmetros (ou o valor inicial). */
export function requiredEvidenceFor(
  settings: { requiredEvidence: Record<string, readonly string[]> } | null | undefined,
  kind: string,
): readonly string[] {
  return (settings?.requiredEvidence ?? FACILITIES_DEFAULT_SETTINGS.requiredEvidence)[kind] ?? [];
}

/** O chamado concluído ainda pode ser reaberto? (dias corridos desde a conclusão) */
export function reopenable(closedAt: Date | null, reopenDays: number, now: number = Date.now()) {
  return !!closedAt && now - closedAt.getTime() < reopenDays * 24 * 60 * 60 * 1000;
}

/** Indicadores da manutenção: período e empresa (opcional). */
export const maintenanceIndicatorsQuery = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  legalEntityId: z.uuid().optional(),
});
